import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ChapterProgressCard = ({
  chapter = {
    id: 1,
    title: "Introduction to AI",
    description: "Fundamental concepts and overview of artificial intelligence",
    presentationProgress: 100,
    quizAttempts: 2,
    bestScore: 85,
    timeSpent: 45, // minutes
    status: "completed",
    isUnlocked: true,
    totalSlides: 12,
    completedSlides: 12,
    lastAccessed: "2024-01-15"
  }
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getStatusIcon = () => {
    switch (chapter.status) {
      case 'completed':
        return 'CheckCircle2';
      case 'in-progress':
        return 'PlayCircle';
      case 'locked':
        return 'Lock';
      default:
        return 'Circle';
    }
  };

  const getStatusColor = () => {
    switch (chapter.status) {
      case 'completed':
        return 'success';
      case 'in-progress':
        return 'secondary';
      case 'locked':
        return 'text-muted';
      default:
        return 'text-secondary';
    }
  };

  const formatTime = (minutes) => {
    if (typeof minutes !== 'number' || isNaN(minutes) || minutes < 0) return '0m';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };


  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getScoreGrade = (score) => {
    if (score >= 90) return 'A';
    if (score >= 80) return 'B';
    if (score >= 70) return 'C';
    if (score >= 60) return 'D';
    return 'F';
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'success';
    if (score >= 80) return 'secondary';
    if (score >= 70) return 'warning';
    return 'error';
  };

  return (
    <div className={`
      bg-surface rounded-xl border transition-all duration-200 ease-out
      ${chapter.status === 'completed' ? 'border-success-200 hover:border-success-300' :
        chapter.status === 'in-progress' ? 'border-secondary-200 hover:border-secondary-300' : 'border-border hover:border-border-focus'}
      hover:shadow-md
    `}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start space-x-3">
            <div className={`
              flex items-center justify-center w-10 h-10 rounded-lg
              ${chapter.status === 'completed' ? 'bg-success-50' :
                chapter.status === 'in-progress' ? 'bg-secondary-50' : 'bg-surface-secondary'}
            `}>
              <Icon
                name={getStatusIcon()}
                size={20}
                color={`var(--color-${getStatusColor()})`}
                strokeWidth={2}
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center mb-1 space-x-2">
                <h3 className="text-lg font-semibold font-heading text-text-primary">
                  Chapter {chapter.id}
                </h3>
                {chapter.status === 'completed' && (
                  <div className="flex items-center px-2 py-1 space-x-1 rounded-full bg-success-100">
                    <Icon name="CheckCircle2" size={12} color="var(--color-success)" strokeWidth={2} />
                    <span className="text-xs font-medium text-success-700">Complete</span>
                  </div>
                )}
              </div>
              <h4 className="mb-1 font-medium text-text-primary">{chapter.title}</h4>
              <p className="text-sm text-text-secondary line-clamp-2">{chapter.description}</p>
            </div>
          </div>

          <Button
            variant="ghost"
            size="sm"
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex-shrink-0"
          />
        </div>

        {/* Progress Overview */}
        <div className="grid grid-cols-2 gap-4 mb-4 lg:grid-cols-4">
          <div className="text-center">
            <div className="text-lg font-bold font-heading text-text-primary">
              {Math.round(chapter.progress)}%
            </div>
            <div className="text-xs text-text-secondary">Presentation</div>
          </div>

          <div className="text-center">
            <div className="text-lg font-bold font-heading text-text-primary">
              {chapter.quizzesCount || 0}
            </div>
            <div className="text-xs text-text-secondary">Quiz Attempts</div>
          </div>

          <div className="text-center">
            <div className={`text-lg font-heading font-bold text-${getScoreColor(chapter.bestScore)}`}>
              {chapter.bestScore || 0}%
            </div>
            <div className="text-xs text-text-secondary">Best Score</div>
          </div>

          <div className="text-center">
            <div className="text-lg font-bold font-heading text-text-primary">
              {formatTime(chapter.timeSpent) || '0m'}
            </div>
            <div className="text-xs text-text-secondary">Time Spent</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-text-secondary">Overall Progress</span>
            <span className="text-sm font-medium text-text-primary">
              {Math.round((chapter.progress + (chapter.bestScore || 0)) / 2)}%
            </span>
          </div>
          <div className="w-full h-2 rounded-full bg-border-light">
            <div
              className={`h-2 rounded-full progress-animation ${chapter.status === 'completed'
                ? 'bg-gradient-to-r from-success to-success-600' : 'bg-gradient-to-r from-secondary to-secondary-600'
                }`}
              style={{ width: `${Math.round((chapter.progress + (chapter.bestScore || 0)) / 2)}%` }}
            />
          </div>
        </div>

        {/* Expanded Details */}
        {isExpanded && (
          <div className="pt-4 space-y-4 border-t border-border">
            {/* Detailed Stats */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">Slides Completed</span>
                  <span className="text-sm font-medium text-text-primary">
                    {chapter.completedSlides}/{chapter.totalSlides}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">Quiz Grade</span>
                  <span className={`text-sm font-bold text-${getScoreColor(chapter.bestScore)}`}>
                    {getScoreGrade(chapter.bestScore)}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">Last Accessed</span>
                  <span className="text-sm font-medium text-text-primary">
                    {formatDate(chapter.lastAccessed)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">Status</span>
                  <span className={`text-sm font-medium capitalize text-${getStatusColor()}`}>
                    {chapter.status.replace('-', ' ')}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2 pt-2 sm:flex-row">
              {chapter.status === 'completed' ? (
                <>
                  <Link to="/interactive-presentation-viewer" className="flex-1">
                    <Button
                      variant="outline"
                      size="sm"
                      fullWidth
                      iconName="RotateCcw"
                      iconPosition="left"
                    >
                      Review Content
                    </Button>
                  </Link>
                  <Link to="/quiz-assessment-interface" className="flex-1">
                    <Button
                      variant="secondary"
                      size="sm"
                      fullWidth
                      iconName="Brain"
                      iconPosition="left"
                    >
                      Retake Quiz
                    </Button>
                  </Link>
                </>
              ) : chapter.status === 'in-progress' ? (
                <Link to="/interactive-presentation-viewer" className="flex-1">
                  <Button
                    variant="primary"
                    size="sm"
                    fullWidth
                    iconName="Play"
                    iconPosition="left"
                  >
                    Continue Learning
                  </Button>
                </Link>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  fullWidth
                  disabled
                  iconName="Lock"
                  iconPosition="left"
                >
                  Locked
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChapterProgressCard;